// ** React Imports
import { useEffect, useState } from "react";

// ** Redux & Store & Actions
import { connect, useSelector } from "react-redux";
import { register } from "store/modules/auth";

// ** Third Party Components
import { Wizard } from "react-use-wizard";
import loadable from "@loadable/component";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { toast } from "react-toastify";

// ** Custom Components
import Block from "components/layout/Block";
import GoogleLogoIcon from "containers/Userpanel/containers/Messages/containers/ShowMsg/components/VideoChat/components/GoogleLogoIcon";
import Text from "components/typography/Text";
import Button from "components/base/Button";

//** Register Components
import StepGender from "containers/Landing/containers/Register/components/StepGender";

import { ButtonOAuth } from "components/layout/Button/ButtonOAuth";
import api from "helpers/ApiClient";
// ** Config
import configGlobal from "configuration";

const StepCoupleGender = loadable(
	() =>
		import(
			"containers/Landing/containers/Register/components/StepCoupleGender"
		),
	{ ssr: false }
);
const StepAge = loadable(
	() => import("containers/Landing/containers/Register/components/StepAge"),
	{ ssr: false }
);
const StepLogin = loadable(
	() => import("containers/Landing/containers/Register/components/StepLogin"),
	{
		ssr: false,
	}
);
const StepEmail = loadable(
	() => import("containers/Landing/containers/Register/components/StepEmail"),
	{
		ssr: false,
	}
);
const StepPassword = loadable(
	() =>
		import(
			"containers/Landing/containers/Register//components/StepPassword"
		),
	{
		ssr: false,
	}
);
const StepLocation = loadable(
	() =>
		import(
			"containers/Landing/containers/Register/components/StepLocation"
		),
	{
		ssr: false,
	}
);
const StepThankYou = loadable(
	() =>
		import(
			"containers/Landing/containers/Register/components/StepThankYou"
		),
	{
		ssr: false,
	}
);

const StyledCloseButton = styled(Button)`
	margin-left: auto;
	font-size: 1rem;
	text-shadow: 0 1px 0 #fff;
	color: rgba(255, 255, 255, 0.65);
	svg {
		color: rgba(255, 255, 255, 0.95);
	}
`;
// #####################################################

const Register = ({
	coupleCase,
	googleAuth,
	setGoogleAuth,
	isModal = false,
}) => {
	const { t } = useTranslation(["landing", "common"]);
	useEffect(() => {
		StepCoupleGender.preload();
		StepAge.preload();
		StepLogin.preload();
		StepPassword.preload();
		StepEmail.preload();
		StepLocation.preload();
		StepThankYou.preload();
	}, []);

	const [showCancelOauthButton, setShowCancelOauthButton] = useState(
		googleAuth.active
	);
	const [showContinueWithOauthButton, setShowContinueWithOauthButton] =
		useState(true);

	const language = useSelector((state) => state.globalCommon.language);

	const hasAnalyticsStorageConsent = useSelector(
		(state) => state.globalCommon.cookieConsent.settings.analytics_storage
	);

	const isClient = useSelector((state) => state.globalCommon.isClient);
	let firebaseHashObject = null;

	try {
		firebaseHashObject =
			isClient && localStorage
				? localStorage.getItem(
						configGlobal.firebase.localStorage.itemName
				  )
				: null;
	} catch (e) {
		firebaseHashObject = null;
	}

	// ** A/B Tests
	const authModalVersion = useSelector(
		(state) => state.globalCommon.authModal.version
	);

	// #####################################################
	return (
		<Block>
			{googleAuth.active && (
				<Block flex alignEnd relative>
					<GoogleLogoIcon customMargin="0 0.5rem 0 0" />{" "}
					<Text mr={2}>{t("landing:registerForFreeWithGoogle")}</Text>
					{showCancelOauthButton && (
						<StyledCloseButton
							label={t("common:cancel")}
							onClick={() => {
								setGoogleAuth((prev) => {
									return {
										...prev,
										active: false,
										reset: true,
									};
								});
							}}
						>
							<Text style={{ lineHeight: "21px" }}>
								{t("common:cancel")}
							</Text>
						</StyledCloseButton>
					)}
				</Block>
			)}

			<Wizard>
				{/* STEP 1 */}
				<StepGender
					currentStep={1}
					totalSteps={!googleAuth.active ? 8 : 7}
					googleAuth={googleAuth}
					setGoogleAuth={setGoogleAuth}
					isModal={isModal}
				/>
				{/* STEP 2  */}
				{coupleCase ? (
					<StepCoupleGender
						currentStep={2}
						totalSteps={!googleAuth.active ? 8 : 7}
						googleAuth={googleAuth}
						setGoogleAuth={setGoogleAuth}
						isModal={isModal}
					/>
				) : (
					<StepAge
						currentStep={2}
						totalSteps={!googleAuth.active ? 8 : 7}
						googleAuth={googleAuth}
						setGoogleAuth={setGoogleAuth}
						isModal={isModal}
					/>
				)}
				{/* STEP 3 */}
				<StepLocation
					currentStep={3}
					totalSteps={!googleAuth.active ? 8 : 7}
					googleAuth={googleAuth}
					setGoogleAuth={setGoogleAuth}
					isModal={isModal}
				/>
				{/* STEP 4 */}
				<StepLogin
					currentStep={4}
					totalSteps={!googleAuth.active ? 8 : 7}
					googleAuth={googleAuth}
					setGoogleAuth={setGoogleAuth}
					isModal={isModal}
				/>

				{/* STEP 5 */}
				{!googleAuth.active && (
					<StepPassword
						currentStep={5}
						totalSteps={8}
						googleAuth={googleAuth}
						setGoogleAuth={setGoogleAuth}
						isModal={isModal}
					/>
				)}
				{/* STEP 6 */}
				<StepEmail
					currentStep={!googleAuth.active ? 6 : 5}
					totalSteps={!googleAuth.active ? 8 : 7}
					googleAuth={googleAuth}
					setGoogleAuth={setGoogleAuth}
					isModal={isModal}
				/>
				{/* STEP 7 */}
				<StepThankYou
					currentStep={!googleAuth.active ? 7 : 6}
					totalSteps={!googleAuth.active ? 8 : 7}
					setShowCancelOauthButton={setShowCancelOauthButton}
					setShowContinueWithOauthButton={
						setShowContinueWithOauthButton
					}
					setGoogleAuth={setGoogleAuth}
					isModal={isModal}
				/>
			</Wizard>

			{/* CPA TESTING */}
			{hasAnalyticsStorageConsent && (
				<img
					id="1000524292_cpa_testing"
					src={`https://ads.trafficjunky.net/ct?a=1000524292&member_id=1000428751&cb=${Date.now()}&ctv=0.05`}
					width="1"
					height="1"
					border="0"
					alt="cpa_testing"
					style={{ visibility: "hidden", display: "block" }}
					loading="eager"
				/>
			)}

			{showContinueWithOauthButton && !googleAuth.active && (
				<Block flex mt={4} justifyEnd>
					<ButtonOAuth
						$outline
						$isModal={isModal}
						disabled={googleAuth.loading}
						loading={googleAuth.loading}
						label={"continueWithGoogle"}
						onClick={() => {
							setGoogleAuth((prev) => ({
								...prev,
								loading: true,
							}));
							api.get("/authorization/oauth/google")
								.then(({ data } = {}) => {
									if (sessionStorage) {
										sessionStorage.setItem(
											"oauthGoogle",
											JSON.stringify({
												token: data.token,
												hashfirebase:
													firebaseHashObject,
												initiator: "login",
												pathname: location.pathname,
												language,
											})
										);
										window.location.href = data.url;
									} else {
										setGoogleAuth((prev) => ({
											...prev,
											token: null,
											loading: false,
										}));
									}
								})
								.catch((error) => {
									setGoogleAuth((prev) => ({
										...prev,
										loading: false,
									}));
									toast.error(error?.response?.data?.error);
								});
						}}
					>
						<Block flex row aligntCenter justifyCenter>
							<GoogleLogoIcon customMargin="0 0.5rem 0 0 " />
							<Text>
								{t("landing:login:continueWithGoogle")}{" "}
							</Text>
						</Block>
					</ButtonOAuth>
				</Block>
			)}
		</Block>
	);
};

// #####################################################

const mapStateToProps = ({
	auth: {
		register: {
			registerForm: {
				gender: { coupleCase },
			},
		},
	},
}) => ({
	coupleCase,
});

export default connect(mapStateToProps, {
	register,
})(Register);

// #####################################################
